import type { MasterKey } from '../../constants/masters.constants';

/**
 * The icon each master's page header shows -- its screen definition's `icon` (`<master>.definition.ts`), which
 * is not always the Masters menu's (`MASTER_DEFINITIONS`): Entity's header is `pi-tabler-building`, its menu
 * entry `pi-building`. Written out rather than imported so the delete dialog does not pull every master's
 * definition into its chunk; `associationIcon.test.ts` holds the two to each other.
 */
export const MASTER_HEADER_ICONS: Readonly<Record<MasterKey, string>> = {
  organization: 'pi-tabler-building-skyscraper',
  entity: 'pi-tabler-building',
  'business-unit': 'pi-tabler-building-estate',
  division: 'pi-tabler-sitemap',
  department: 'pi-tabler-users-group',
  location: 'pi-tabler-building-factory-2',
  category: 'pi-tabler-category',
  uom: 'pi-tabler-ruler-2',
  country: 'pi-globe',
  state: 'pi-tabler-map-2',
  city: 'pi-tabler-building-community',
  material: 'pi-tabler-package',
  currency: 'pi-currency-coins',
  'payment-terms': 'pi-payment-term',
  'delivery-terms': 'pi-tabler-truck-delivery',
  incoterm: 'pi-send',
  pr: 'pi-clipboard',
  supplier: 'pi-briefcase',
  designation: 'pi-id-card',
  'user-management': 'pi-tabler-user-cog',
};

/**
 * The names the platform gives associated records (`AssociationConflict` labels: "Legal entities", "Location
 * mappings", "Units of measurement", ...), normalised, against the master whose header icon each takes.
 */
const MASTER_OF: Readonly<Record<string, MasterKey>> = {
  organization: 'organization',
  entity: 'entity',
  'legal entity': 'entity',
  'business unit': 'business-unit',
  division: 'division',
  department: 'department',
  location: 'location',
  category: 'category',
  'product category': 'category',
  'unit of measurement': 'uom',
  uom: 'uom',
  country: 'country',
  state: 'state',
  city: 'city',
  product: 'material',
  material: 'material',
  currency: 'currency',
  'payment term': 'payment-terms',
  'delivery term': 'delivery-terms',
  incoterm: 'incoterm',
  'purchase request': 'pr',
  'purchase requisition': 'pr',
  supplier: 'supplier',
  designation: 'designation',
  user: 'user-management',
};

/** Records that are not masters, with the icon the application already gives them (its menu's). */
const OTHER_ICONS: Readonly<Record<string, string>> = {
  bid: 'pi-file',
  template: 'pi-clone',
  'workflow configuration': 'pi-sitemap',
  'site preference': 'pi-sliders-h',
  /* User Management's delete refusals: the users reporting to them (2026-10-15), and their own manager (2026-10-23). */
  'reporting user': 'pi-users',
  'reporting manager': 'pi-user',
};

/** What a row shows when its kind of record is none of the above. */
export const DEFAULT_ASSOCIATION_ICON = 'pi-link';

/** "Location mappings" -> "location", "Units of measurement" -> "unit of measurement", "Suppliers" -> "supplier". */
function normalise(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/\s+(mappings?|links?)$/, '')
    .split(/\s+/)
    .map((word) => {
      if (word.endsWith('ies')) return `${word.slice(0, -3)}y`;
      if (word.endsWith('s') && !word.endsWith('ss') && !word.endsWith('us')) return word.slice(0, -1);
      return word;
    })
    .join(' ');
}

/**
 * The PrimeIcons name a "Can't delete" row shows beside an associated kind of record: the header icon of the
 * master those records belong to, the application's own icon for the few that are not masters, and the link
 * otherwise.
 */
export function associationIcon(label: string): string {
  const name = normalise(label);
  const master = MASTER_OF[name];
  if (master) return MASTER_HEADER_ICONS[master];
  return OTHER_ICONS[name] ?? DEFAULT_ASSOCIATION_ICON;
}
